import { useEffect, useState } from "react";
import { Alert, Form, Table, Badge } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { ReportMasthead } from "../components/reports/ReportDocument.jsx";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { FilterSelect, ClaimLink } from "../components/reports/reportShared.jsx";
import { getImportantJobsReport, listEntityRows, listUsers } from "../services/apiClient.js";
import { formatDate } from "../utils/date.js";

// Important Jobs (legacy ImportantJobsReport.aspx): jobs flagged Important
// and/or Problem, by PCO/division; auto-searches on every change, as 1.0.
// 1.0 fixes: parameterised (SQL injection), row cap added.

const COLUMN_DEFS = [
  ["job", "Job #"], ["pco", "Project Co-Ordinator"], ["division", "Division"],
  ["type", "Type"], ["status", "Status"], ["address", "Address"],
  ["progress", "Last Progress"], ["problem", "Problem Date"], ["note", "RDR Note"],
];
const COLS_KEY = "importantJobsCols:v1";

function ImportantJobsReportPage() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") || "important");
  const [pco, setPco] = useState("");
  const [division, setDivision] = useState("");
  const [users, setUsers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const loadCols = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLS_KEY));
      if (Array.isArray(saved)) return new Set(saved);
    } catch { /* fall through */ }
    return new Set(COLUMN_DEFS.map(([k]) => k));
  };
  const [cols, setCols] = useState(loadCols);
  const col = (k) => cols.has(k);
  const toggleCol = (k) => {
    setCols((c) => {
      const next = new Set(c);
      if (next.has(k)) next.delete(k); else next.add(k);
      localStorage.setItem(COLS_KEY, JSON.stringify([...next]));
      return next;
    });
  };
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    listUsers({ pco: true, activeOnly: true }).then(setUsers).catch(() => {});
    listEntityRows("divisions").then(setDivisions).catch(() => {});
  }, []);

  useEffect(() => {
    setError("");
    getImportantJobsReport({ mode, project_coordinator_id: pco, division_id: division })
      .then(setReport)
      .catch((err) => setError(err.response?.data?.error || "Unable to load the report."));
  }, [mode, pco, division]);

  // What this copy covers. This report is filtered by coordinator and division
  // rather than by client, so the scope names those rather than a company.
  const nameOf = (list, id, field = "name") =>
    (id && (list || []).find((r) => String(r.id) === String(id))?.[field]) || null;
  const scopeLabel =
    { important: "Important jobs", problem: "Problem jobs", both: "Important and problem jobs" }[mode] ||
    "Important jobs";
  const subtitle = [
    "Grayburn Property Services",
    nameOf(users, pco, "full_name"),
    nameOf(divisions, division),
  ].filter(Boolean).join(" · ");

  const badge = (t) =>
    t === "Important & Problem Job" ? "danger" : t === "Problem Job" ? "warning" : "info";

  return (
    <section className="page-panel">
      {/* App chrome: the document carries its own masthead (report-document.css). */}
      <div className="page-heading no-print"><h2>Problem &amp; Important Jobs <RulesHelp topic="importantJobs" /></h2></div>

      <div className="report-controls d-flex align-items-end column-gap-3 row-gap-2 flex-wrap mb-3 no-print">
        <div>
          <Form.Label className="mb-1 small text-muted d-block">Show</Form.Label>
          {[["important", "Important Jobs"], ["problem", "Problem Jobs"], ["both", "Both"]].map(([v, label]) => (
            <Form.Check
              inline type="radio" key={v} id={`ij-${v}`} name="ij-mode" label={label}
              checked={mode === v} onChange={() => setMode(v)}
            />
          ))}
        </div>
        <FilterSelect label="Project Co-Ordinator" value={pco} onChange={setPco} options={users} labelField="full_name" width={200} />
        <FilterSelect label="Division" value={division} onChange={setDivision} options={divisions} width={170} />
      </div>

      <div className="d-flex align-items-center column-gap-3 flex-wrap mb-2 no-print">
        <span className="small fw-semibold text-muted">Show columns:</span>
        {COLUMN_DEFS.map(([k, label]) => (
          <Form.Check
            key={k} inline type="checkbox" id={`ij-col-${k}`} label={label}
            className="small" checked={cols.has(k)} onChange={() => toggleCol(k)}
          />
        ))}
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!report ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <div className="report-doc report-doc-wide border rounded">
          <ReportMasthead report="Problem & Important Jobs" title={scopeLabel} subtitle={subtitle} />
          <div className="report-body">
          <Table striped bordered hover responsive size="sm" className="align-middle report-table">
            <thead className="table-surface">
              <tr>
                {col("job") ? <th>Job #</th> : null}
                {col("pco") ? <th>Project Co-Ordinator</th> : null}
                {col("division") ? <th>Division</th> : null}
                {col("type") ? <th>Type</th> : null}
                {col("status") ? <th>Status</th> : null}
                {col("address") ? <th>Address</th> : null}
                {col("progress") ? <th className="text-nowrap">Last Progress</th> : null}
                {col("problem") ? <th className="text-nowrap">Problem Date</th> : null}
                {col("note") ? <th>RDR Note</th> : null}
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={r.claim_id}>
                  {col("job") ? <td><ClaimLink claimId={r.claim_id} claimNumber={r.claim_number} /></td> : null}
                  {col("pco") ? <td className="text-nowrap">{r.project_coordinator_name || "-"}</td> : null}
                  {col("division") ? <td className="text-nowrap">{r.division_name || "-"}</td> : null}
                  {col("type") ? <td><Badge bg={badge(r.search_type)}>{r.search_type}</Badge></td> : null}
                  {col("status") ? <td className="text-nowrap">{r.status_name}</td> : null}
                  {col("address") ? <td className="text-truncate" style={{ maxWidth: 220 }} data-hint={r.street_address || undefined}>{r.street_address || "-"}</td> : null}
                  {col("progress") ? <td className="text-nowrap">{r.last_progress_date ? formatDate(r.last_progress_date) : "Not Entered"}</td> : null}
                  {col("problem") ? <td className="text-nowrap">{r.problem_date_occurred ? formatDate(r.problem_date_occurred) : "-"}</td> : null}
                  {/* The RDR note WRAPS rather than truncating (Robert 2026-08-01):
                      it is the reason the job is on this list, so a hover tooltip is
                      no use on paper and half a sentence is no use anywhere. */}
                  {col("note") ? <td className="report-col-main" style={{ whiteSpace: "pre-wrap" }}>{r.rdr_note || "-"}</td> : null}
                </tr>
              ))}
              {report.rows.length === 0 ? (
                <tr><td colSpan={cols.size} className="text-center text-muted">No jobs match.</td></tr>
              ) : null}
            </tbody>
          </Table>
          </div>
        </div>
      )}
    </section>
  );
}

export default ImportantJobsReportPage;
